/** Angular */
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
/** rxjs */
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
/** Interfaces */
import { BydAbstractServiceInterface } from '../interfaces/byd-abstract-service-interface';
import { BydHttpOptionsInterface } from '../interfaces/byd-http-options.interface';
/** Models */
import { BydModel } from '../models/byd-model';
/** Types */
import { BydHttpMethodsType } from '../types/byd-http-methods.type';
/** Enums */
import { BydHttpMethodsEnum } from '../enums/byd-http-methods.enum';
/** Services */
import { BydConfigService } from './byd-config.service';

/**
 * Performs HTTP requests.
 * This service is available as an inheritance or injectable class, with methods to perform HTTP requests.
 * Each request method has multiple signatures, and the return type varies based on
 * the signature that is called (mainly the values of `observe` and `responseType`).
 *
 * @abstract
 */
@Injectable({
  providedIn: 'root',
})
export abstract class BydAbstractService implements BydAbstractServiceInterface {
  /**
   * Constructs a new HTTP headers object in the child abstract service.
   */
  protected headers: HttpHeaders;

  /**
   * The abstract api base url.
   *
   * @abstract
   */
  public abstract baseUrl: string;

  /**
   * The abstract endpoints `resources` path.
   *
   * @abstract
   */
  public abstract path: string;

  /**
   * @param http Perform HTTP requests
   * @param bydConfigService The shared configurations service
   */
  // TODO:  Set up the correct type
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  constructor(protected http: HttpClient, protected bydConfigService: BydConfigService<any>) {}

  /**
   * Simplified request to GET with HttpClient.
   *
   * @param httpRequestOptions The http request options `BydHttpOptionsInterface`
   * @returns Observable
   */
  protected get<T>(httpRequestOptions?: BydHttpOptionsInterface<T>): Observable<T> {
    return this.requestApi(BydHttpMethodsEnum.GET, httpRequestOptions);
  }

  /**
   * Simplified request to PUT with HttpClient.
   *
   * @param httpRequestOptions The http request options `BydHttpOptionsInterface`
   * @returns Observable
   */
  protected put<T>(httpRequestOptions?: BydHttpOptionsInterface<T>): Observable<T> {
    return this.requestApi<T>(BydHttpMethodsEnum.PUT, httpRequestOptions);
  }

  /**
   * Simplified request to PATCH with HttpClient.
   *
   * @param httpRequestOptions The http request options `BydHttpOptionsInterface`
   * @returns Observable
   */
  protected patch<T>(httpRequestOptions?: BydHttpOptionsInterface<T>): Observable<T> {
    return this.requestApi<T>(BydHttpMethodsEnum.PATCH, httpRequestOptions);
  }

  /**
   * Simplified request to POST with HttpClient.
   *
   * @param httpRequestOptions The http request options `BydHttpOptionsInterface`
   * @returns an `Observable` of the `HttpResponse`, with a body type of `T`.
   */
  protected post<T>(httpRequestOptions?: BydHttpOptionsInterface<T>): Observable<T> {
    return this.requestApi<T>(BydHttpMethodsEnum.POST, httpRequestOptions);
  }

  /**
   * Simplified request to DELETE with HttpClient.
   *
   * @param httpRequestOptions The http request options `BydHttpOptionsInterface`
   * @returns an `Observable` of the `HttpResponse`, with a body type of `T`.
   */
  protected delete<T>(httpRequestOptions?: BydHttpOptionsInterface<T>): Observable<T> {
    return this.requestApi<T>(BydHttpMethodsEnum.DELETE, httpRequestOptions);
  }

  /**
   * Get models request.
   *
   * @param httpRequestOptions The http request options `BydHttpOptionsInterface`
   * @returns Observable type `<BydModel<T>>`
   */
  protected getModels<T>(httpRequestOptions?: BydHttpOptionsInterface<BydModel<T>>): Observable<BydModel<T>> {
    return this.requestApi<BydModel<T>>(BydHttpMethodsEnum.GET, httpRequestOptions).pipe(
      map(
        // TODO:  Set up the correct type
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (response: any) =>
          new BydModel<T>({
            models: response.data ? response.data.map(this.createNewInstance) : response.map(this.createNewInstance),
            totalModels: response.numRows,
            numPerPage: response.numPerPage,
            totalPages: response.numPages,
            pageNumber: response.pageNumber,
          })
      )
    );
  }

  /**
   * Get model request.
   *
   * @param httpRequestOptions The http request options `BydHttpOptionsInterface`
   * @returns Observable with a type of `<T>`
   */
  protected getModel<T>(httpRequestOptions?: BydHttpOptionsInterface<T>): Observable<T> {
    return this.requestApi<T>(BydHttpMethodsEnum.GET, httpRequestOptions).pipe(map(this.createNewInstance));
  }

  /**
   * Create a model request.
   *
   * @param httpRequestOptions the http request options `BydHttpOptionsInterface`
   * @returns Observable type `<T>`
   */
  protected postModel<T>(httpRequestOptions?: BydHttpOptionsInterface<T>): Observable<T> {
    return this.requestApi<T>(BydHttpMethodsEnum.POST, httpRequestOptions).pipe(map(this.createNewInstance));
  }

  /**
   * Update a model request.
   *
   * @param httpRequestOptions the http request options `BydHttpOptionsInterface`
   * @returns Observable type `<T>`
   */
  protected patchModel<T>(httpRequestOptions?: BydHttpOptionsInterface<T>): Observable<T> {
    return this.requestApi<T>(BydHttpMethodsEnum.PATCH, httpRequestOptions).pipe(map(this.createNewInstance));
  }

  /**
   * Create a models request.
   *
   * @param httpRequestOptions The http request options `BydHttpOptionsInterface`
   * @returns Observable type `<BydModel<T>>`
   */
  protected postModels<T>(httpRequestOptions?: BydHttpOptionsInterface<BydModel<T>>): Observable<BydModel<T>> {
    return this.requestApi<BydModel<T>>(BydHttpMethodsEnum.POST, httpRequestOptions).pipe(
      map(
        // TODO:  Set up the correct type
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (response: any) =>
          new BydModel<T>({
            models: response.data ? response.data.map(this.createNewInstance) : response.map(this.createNewInstance),
            totalModels: response.numRows,
          })
      )
    );
  }

  /**
   * Update a model request.
   *
   * @param httpRequestOptions The http request options `BydHttpOptionsInterface`
   * @returns Observable type `<T>`
   */
  protected putModel<T>(httpRequestOptions?: BydHttpOptionsInterface<T>): Observable<T> {
    return this.requestApi<T>(BydHttpMethodsEnum.PUT, httpRequestOptions).pipe(map(this.createNewInstance));
  }

  /**
   * Update a models request.
   *
   * @param httpRequestOptions The http request options `BydHttpOptionsInterface`
   * @returns Observable type `<BydModel<T>>`
   */
  protected putModels<T>(httpRequestOptions?: BydHttpOptionsInterface<BydModel<T>>): Observable<BydModel<T>> {
    return this.requestApi<BydModel<T>>(BydHttpMethodsEnum.PUT, httpRequestOptions).pipe(
      map(
        // TODO:  Set up the correct type
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (response: any) =>
          new BydModel<T>({
            models: response.data ? response.data.map(this.createNewInstance) : response.map(this.createNewInstance),
            totalModels: response?.numRows,
          })
      )
    );
  }

  /**
   * Perform HTTP requests.
   *
   * @param method The http method to call 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
   * @param httpRequestOptions The http request options `BydHttpOptionsInterface`
   * @returns an `Observable` of the `HttpResponse`, with a body type of `T`.
   */
  protected requestApi<R>(method: BydHttpMethodsType, httpRequestOptions: BydHttpOptionsInterface<R>): Observable<R> {
    return this.http.request<R>(method, `${this.baseUrl}${this.path}`, {
      ...httpRequestOptions,
      headers: this.headers
        ? this.mergeHeaders(httpRequestOptions?.headers)
        : httpRequestOptions?.headers || this.bydConfigService.headers,
    });
  }

  /**
   * Merge shared & specific layer http request headers.
   *
   * The `httpRequestOptions` headers & the specific headers from the child abstract service.
   *
   * @param headers The headers object from the child abstract service
   * @returns HttpHeaders
   */
  private mergeHeaders(headers = this.bydConfigService.headers) {
    let mergedHeaders = headers;
    this.headers.keys().forEach((key) => {
      mergedHeaders = mergedHeaders.set(key, this.headers.get(key));
    });
    return mergedHeaders;
  }

  /**
   * The abstract create instance method.
   *
   * @param jsonObject The json object goes to the createNewInstance method in the specified service.
   * @returns Generic instance
   */
  // TODO:  Set up the correct type
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public abstract createNewInstance(jsonObject: any): any;
}
